import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { Box } from "@mui/material";
import { patchInvestigation } from "../../../../../apis";
import { ValidateSoeid } from "./Modals/ValidateSoeid";
import {
  applySoeidToEmployment,
  employmentFromInvolved,
} from "./employment";
import { InvestigationSectionHeader } from "../components/InvestigationSectionHeader";
import { InvestigationAdvertisement } from "../components/InvestigationAdvertisement";
import { Separator } from "../components";
import { InvolvedInfo } from "./Modals/InvolvedInfo";
import { AddInvolved } from "./AddInvolved";
import { InvolvedTypes } from "./InvolvedTypes";
import { useInvolveds } from "./hooks/useInvolveds";
import { useConstants } from "../Main/useConstants";

const initInvolveds = {
  victim: [],
  witness: [],
  offender: [],
  accomplice: [],
};

// Componente de involucrados
export const Involved = ({
  investigation,
  setInvestigation,
  justView,
  handleSelectedSection,
}) => {
  const [t] = useTranslation("investigation");
  const {
    enums,
    functions: { askUpdateInvestigationContext },
  } = useConstants();

  const [involveds, setInvolveds] = useState(initInvolveds);

  const [isAdding, setIsAdding] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  // La lista muestra el detalle en linea, asi que el drawer se abre en edicion
  const [drawerInEdit, setDrawerInEdit] = useState(false);
  // Involucrado al que se le esta validando el SOEID (null = modal cerrado)
  const [soeidInvolved, setSoeidInvolved] = useState(null);
  const [selectedInvolved, setSelectedInvolved] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const haveInvolveds = useMemo(() => {
    return Object.values(involveds).some((involved) => involved.length > 0);
  }, [involveds]);

  useEffect(() => {
    if (investigation.involved) {
      setInvolveds(investigation.involved);
    } else {
      setInvolveds(initInvolveds);
    }
  }, [investigation.involved]);

  const handleSaveInvolveds = async (newInvolveds) => {
    const objToSave = {
      folder: {
        involved: newInvolveds,
      },
    };

    setIsLoading(true);
    try {
      await patchInvestigation(investigation.alias, objToSave);
      const updatedInvestigation = {
        ...investigation,
        involved: newInvolveds,
      };
      setInvestigation(updatedInvestigation);
      // Refresca los formatted arrays (involvedsVictimInfo, etc.) que lee la
      // sección de Entrevistas para el selector de entrevistado.
      askUpdateInvestigationContext(updatedInvestigation);
      setIsAdding(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Guarda el registro validado dentro del involucrado, en la llave `soeid`.
  // El nombre del involucrado se reemplaza por el de nomina: se captura a mano
  // y suele venir incompleto o con erratas; el validado es el que empata con
  // los sistemas del banco.
  const handleConfirmSoeid = async (soeidData) => {
    const { kind, index } = soeidInvolved ?? {};
    const bucket = involveds?.[kind];
    if (!Array.isArray(bucket) || !(index >= 0 && index < bucket.length)) {
      console.error("No se pudo ubicar al involucrado para guardar el SOEID");
      return;
    }

    const newInvolveds = _.cloneDeep(involveds);
    const involved = newInvolveds[kind][index];
    newInvolveds[kind][index] = {
      ...involved,
      // Solo si el registro trae nombre: nunca dejar al involucrado sin uno.
      name: soeidData?.full_name || involved.name,
      soeid: soeidData,
      // Rellena los datos laborales que sigan vacios; nunca pisa lo capturado
      ...applySoeidToEmployment(employmentFromInvolved(involved), soeidData),
    };

    await handleSaveInvolveds(newInvolveds);
    setSoeidInvolved(null);
  };

  return (
    <>
      <ValidateSoeid // MODAL
        show={!!soeidInvolved}
        onHide={() => setSoeidInvolved(null)}
        involved={soeidInvolved}
        onConfirm={handleConfirmSoeid}
        isLoading={isLoading}
      />

      <InvolvedInfo // MODAL
        open={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setDrawerInEdit(false);
        }}
        openInEdit={drawerInEdit}
        selectedInvolved={selectedInvolved}
        setSelectedInvolved={setSelectedInvolved}
        handleSaveInvolveds={handleSaveInvolveds}
        involvedsInInvestigation={involveds}
        isLoading={isLoading}
        justView={justView}
      />

      <Box className="dyTheme1" p={1}>
        {!isAdding && !justView && (
          <InvestigationSectionHeader
            text={t("involved.add_to_report")}
            buttonText={t("involved.add")}
            buttonIcon="outline_user_circle_add"
            onClick={() => setIsAdding(true)}
            type="involved"
          />
        )}

        {justView && (
          <Separator
            text={t("sections.involved")}
            icon="outline_profile_2_user"
            iconButton={handleSelectedSection ? "outline_edit" : undefined}
            onClick={
              handleSelectedSection
                ? () => handleSelectedSection(enums.sections.involveds)
                : undefined
            }
            sx={{ marginBottom: 1.5 }}
          />
        )}

        {!haveInvolveds && !isAdding && (
          <InvestigationAdvertisement
            title={t("involved.no_registered")}
            description={t("involved.review_and_include")}
          />
        )}

        {haveInvolveds && !isAdding && (
          <InvolvedTypes
            // involvedsInInvestigation={involvedsInInvestigation}
            involvedsInInvestigation={involveds}
            setSelectedInvolved={setSelectedInvolved}
            setShowDrawer={setShowDrawer}
            setDrawerInEdit={setDrawerInEdit}
            onValidateSoeid={setSoeidInvolved}
            justView={justView}
          />
        )}

        {isAdding && (
          <AddInvolved
            handleSaveInvolveds={handleSaveInvolveds}
            // involvedsInInvestigation={involvedsInInvestigation}
            involvedsInInvestigation={involveds}
            setIsAdding={setIsAdding}
            isLoading={isLoading}
          />
        )}
      </Box>
    </>
  );
};
